/**
 * CommitBoard — the import session's overview AND its commit surface
 * (GDN-2253 / GDN-2256).
 *
 * One table, one row per resource type, ordered by dependency. The ordering
 * is server-derived: the staging summary's `waits_for` maps each type to the
 * session types its rows reference (meds → patients, documents → encounters),
 * so a new resource type slots itself in with no code change here. Each row
 * shows what was ingested (staged / committed / skipped), what still needs a
 * human (decisions, provider mapping), and carries its own Commit button — a
 * type commits when its own gates pass AND everything it waits for is fully
 * committed. When the only thing holding a patient-child type back is a
 * partially-committed patient wave, the button becomes "Commit ready (N)":
 * it commits the rows whose patient is already committed (scoped, GDN-2276)
 * and leaves the rest staged, so dependents follow patients progressively
 * instead of waiting for every patient to resolve. The disabled button's
 * tooltip always says why.
 *
 * Every row is also a door: clicking it opens the Review surface scoped to
 * that type (the host owns that navigation).
 *
 * Presentational: the host supplies rows + waits_for and dispatches commits.
 */
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import {
  Box,
  Button,
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

export interface CommitBoardRow {
  resourceType: string;
  staged: number;
  committed: number;
  skipped: number;
  invalid: number;
  /** Undecided conflicts of this type (patients also count open matches). */
  openDecisions: number;
  /** Unmapped required providers — only ever set on encounter rows. */
  unmappedProviders?: number;
}

export interface CommitBoardProps {
  rows: CommitBoardRow[];
  /**
   * Source providers, rendered as a first-class board row (they're a task
   * with a gate, like any type): "N to map" chips and a door into the
   * mapping surface. Absent = no provider row (read-only contexts).
   */
  providers?: { total: number; unmapped: number };
  onOpenProviders?: () => void;
  /**
   * Server-derived dependencies: type -> the session types it must wait for
   * at commit (the staging summary's waits_for, GDN-2256).
   */
  waitsFor: Record<string, string[]>;
  /**
   * Per type, how many still-staged rows have an already-committed patient —
   * i.e. would commit now under a scoped (progressive) run (the staging
   * summary's commit_ready_by_type, GDN-2276). Drives the "Commit ready (N)"
   * affordance and the "N ready · M waiting" hint.
   */
  commitReadyByType: Record<string, number>;
  /** Commit every staged row of one type (all parents fully committed). */
  onCommitType: (resourceType: string) => void;
  /**
   * Commit only the rows of one type whose patient is already committed
   * (scope_to_committed_patients); the rest stay staged (GDN-2276).
   */
  onCommitReady: (resourceType: string) => void;
  /** Open the Review surface scoped to this type. */
  onOpenType: (resourceType: string) => void;
  commitInFlight: boolean;
}

const typeLabel = (rt: string) => rt.replaceAll('_', ' ');

/**
 * The commit affordance for a row:
 *  - done: nothing left staged, some committed.
 *  - blocked: can't commit now (reason says why).
 *  - commit: every staged row can commit — parents fully committed.
 *  - commit-ready: only a partially-committed patient wave holds it back, and
 *    `ready` rows can commit now (scoped); `waiting` stay staged (GDN-2276).
 */
type CommitState =
  | { kind: 'done' }
  | { kind: 'blocked'; reason: string }
  | { kind: 'commit' }
  | { kind: 'commit-ready'; ready: number; waiting: number };

function commitState(
  row: CommitBoardRow,
  rows: CommitBoardRow[],
  waitsFor: Record<string, string[]>,
  commitReadyByType: Record<string, number>,
): CommitState {
  if (row.staged === 0)
    return row.committed > 0
      ? { kind: 'done' }
      : { kind: 'blocked', reason: 'Nothing staged' };
  if (row.openDecisions > 0)
    return {
      kind: 'blocked',
      reason: `${row.openDecisions.toLocaleString()} decision${
        row.openDecisions > 1 ? 's' : ''
      } outstanding`,
    };
  if ((row.unmappedProviders ?? 0) > 0)
    return {
      kind: 'blocked',
      reason: `${row.unmappedProviders} provider${
        (row.unmappedProviders ?? 0) > 1 ? 's' : ''
      } unmapped`,
    };

  const blockingParents = (waitsFor[row.resourceType] ?? [])
    .map((parent) => rows.find((r) => r.resourceType === parent))
    .filter((parent): parent is CommitBoardRow => !!parent && parent.staged > 0);

  // Every parent fully committed -> the whole type can commit.
  if (blockingParents.length === 0) return { kind: 'commit' };

  // Progressive commit applies only when the sole thing holding this type back
  // is a partially-committed patient wave AND some of its rows are ready now.
  const onlyPatientBlocks = blockingParents.every(
    (parent) => parent.resourceType === 'patient',
  );
  const ready = commitReadyByType[row.resourceType] ?? 0;
  if (onlyPatientBlocks && ready > 0)
    return { kind: 'commit-ready', ready, waiting: row.staged - ready };

  const blocker = blockingParents[0];
  return {
    kind: 'blocked',
    reason: `Commit ${typeLabel(blocker.resourceType)} rows first (${blocker.staged.toLocaleString()} still staged)`,
  };
}

/**
 * Dependency order: types sort after everything they (transitively) wait
 * for; ties break alphabetically so the board is stable. `path` guards
 * cycles along the CURRENT chain only — each sibling branch gets its own
 * copy, so a diamond (two parents sharing an ancestor) still computes the
 * ancestor's real depth instead of short-circuiting to 0.
 */
function depthOf(
  rt: string,
  waitsFor: Record<string, string[]>,
  path: Set<string> = new Set(),
): number {
  if (path.has(rt)) return 0; // cycle guard — data error, don't hang
  const parents = waitsFor[rt] ?? [];
  if (parents.length === 0) return 0;
  const next = new Set(path);
  next.add(rt);
  return 1 + Math.max(...parents.map((p) => depthOf(p, waitsFor, next)));
}

export function CommitBoard({
  rows,
  providers,
  onOpenProviders,
  waitsFor,
  commitReadyByType,
  onCommitType,
  onCommitReady,
  onOpenType,
  commitInFlight,
}: CommitBoardProps) {
  const sorted = [...rows].sort(
    (a, b) =>
      depthOf(a.resourceType, waitsFor) - depthOf(b.resourceType, waitsFor) ||
      a.resourceType.localeCompare(b.resourceType),
  );
  return (
    <Table size="small" sx={{ mb: 3 }}>
      <TableHead>
        <TableRow>
          <TableCell>Resource type</TableCell>
          <TableCell align="right">staged</TableCell>
          <TableCell align="right">committed</TableCell>
          <TableCell align="right">skipped</TableCell>
          <TableCell>Waits for</TableCell>
          <TableCell>Needs a human</TableCell>
          <TableCell align="right" />
        </TableRow>
      </TableHead>
      <TableBody>
        {providers && providers.total > 0 && (
          <TableRow
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() => onOpenProviders?.()}
          >
            <TableCell>
              <Link
                component="button"
                underline="hover"
                color="inherit"
                sx={{ font: 'inherit' }}
              >
                source providers
              </Link>
            </TableCell>
            <TableCell align="right">{providers.total.toLocaleString()}</TableCell>
            <TableCell align="right">—</TableCell>
            <TableCell align="right">—</TableCell>
            <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
            <TableCell>
              {providers.unmapped > 0 ? (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={`${providers.unmapped} to map`}
                />
              ) : (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  icon={<CheckCircleOutlinedIcon fontSize="small" />}
                  label="Mapped"
                />
              )}
            </TableCell>
            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
              <Button size="small" onClick={() => onOpenProviders?.()}>
                Map
              </Button>
            </TableCell>
          </TableRow>
        )}
        {sorted.map((row) => {
          const state = commitState(row, sorted, waitsFor, commitReadyByType);
          const parents = waitsFor[row.resourceType] ?? [];
          return (
            <TableRow
              key={row.resourceType}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => onOpenType(row.resourceType)}
            >
              <TableCell sx={{ textTransform: 'capitalize' }}>
                <Link
                  component="button"
                  underline="hover"
                  color="inherit"
                  sx={{ font: 'inherit' }}
                >
                  {typeLabel(row.resourceType)}
                </Link>
                {row.invalid > 0 && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="error"
                    sx={{ ml: 1 }}
                  >
                    {row.invalid} unreadable
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                {row.staged.toLocaleString()}
              </TableCell>
              <TableCell align="right">
                {row.committed.toLocaleString()}
              </TableCell>
              <TableCell align="right">
                {row.skipped.toLocaleString()}
              </TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                {parents.length > 0 ? parents.map(typeLabel).join(', ') : '—'}
              </TableCell>
              <TableCell>
                {row.openDecisions > 0 ? (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`${row.openDecisions.toLocaleString()} to decide`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenType(row.resourceType);
                    }}
                  />
                ) : (row.unmappedProviders ?? 0) > 0 ? (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`${row.unmappedProviders} provider${
                      (row.unmappedProviders ?? 0) > 1 ? 's' : ''
                    } to map`}
                  />
                ) : null}
              </TableCell>
              <TableCell
                align="right"
                sx={{ whiteSpace: 'nowrap' }}
                onClick={(e) => e.stopPropagation()}
              >
                {state.kind === 'done' ? (
                  <Chip
                    size="small"
                    color="success"
                    icon={<CheckCircleOutlinedIcon fontSize="small" />}
                    label="Committed"
                  />
                ) : state.kind === 'commit-ready' ? (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 0.25,
                    }}
                  >
                    <Tooltip
                      title={`${state.waiting.toLocaleString()} waiting on their patient`}
                    >
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={commitInFlight}
                          onClick={() => onCommitReady(row.resourceType)}
                        >
                          Commit ready ({state.ready.toLocaleString()})
                        </Button>
                      </span>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                      {state.ready.toLocaleString()} ready ·{' '}
                      {state.waiting.toLocaleString()} waiting
                    </Typography>
                  </Box>
                ) : (
                  <Tooltip
                    title={state.kind === 'blocked' ? state.reason : ''}
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={commitInFlight || state.kind === 'blocked'}
                        onClick={() => onCommitType(row.resourceType)}
                      >
                        Commit
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          );
        })}
        {sorted.length === 0 && (
          <TableRow>
            <TableCell colSpan={7}>
              <Box sx={{ py: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Nothing staged yet.
                </Typography>
              </Box>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
